import { useState, useRef, useEffect, useMemo } from 'react'
import { Send, Copy, Download, Play, Trash2, Code, Terminal, X, Loader2, ChevronDown, Paperclip, Upload, Camera, Link, FileText, Image, Share2, RefreshCw, Palette, Wand2 } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import toast from 'react-hot-toast'
import client from '../api/client'
import { useAuthStore } from '../store/authStore'
import { useTokenStore } from '../store/tokenStore'
import CanvasPreview from '../components/CanvasPreview'

const LANGUAGE_NAMES: Record<string, string> = {
  python: 'Python', javascript: 'JavaScript', typescript: 'TypeScript', bash: 'Bash', c: 'C',
  cpp: 'C++', rust: 'Rust', go: 'Go', ruby: 'Ruby', php: 'PHP', java: 'Java', r: 'R',
  perl: 'Perl', swift: 'Swift',
}

const EXECUTABLE_LANGUAGES = Object.keys(LANGUAGE_NAMES)

interface AiModelItem {
  model_name: string
  display_name: string | null
  provider: string
  category: string
  is_paid: boolean
  cost_per_million_usd: number | null
}

interface CodeMessage {
  id: string
  role: 'user' | 'assistant'
  text: string
  blocks?: { language: string; code: string }[]
}

interface ExecutionResult {
  output: string
  stderr: string
  error: string | null
  execution_time_ms: number | null
}

const LS_PREFIX = 'neuralspace_code_'

const loadState = () => {
  try { return JSON.parse(localStorage.getItem(`${LS_PREFIX}state`) || 'null') } catch { return null }
}
const saveState = (state: any) => {
  try { localStorage.setItem(`${LS_PREFIX}state`, JSON.stringify(state)) } catch {}
}

export default function CodePage() {
  const saved = loadState()
  const [messages, setMessages] = useState<CodeMessage[]>(saved?.messages || [])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [editorCode, setEditorCode] = useState(saved?.editorCode || '')
  const [showExample, setShowExample] = useState(!saved?.editorCode)
  const editorOutputRef = useRef<HTMLDivElement>(null)
  const isResizingOutput = useRef(false)
  const [editorLang, setEditorLang] = useState(saved?.editorLang || 'python')
  const [isExecuting, setIsExecuting] = useState(false)
  const [execResult, setExecResult] = useState<ExecutionResult | null>(null)
  const [activeTab, setActiveTab] = useState<'chat' | 'editor' | 'canvas'>(saved?.activeTab || 'chat')
  const [showLangPicker, setShowLangPicker] = useState(false)
  const [showAttachMenu, setShowAttachMenu] = useState(false)
  const [attachedFiles, setAttachedFiles] = useState<{name: string; data: string; mimetype: string}[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)

  const [availableModels, setAvailableModels] = useState<AiModelItem[]>([])
  const [modelsLoading, setModelsLoading] = useState(true)
  const [showModelPicker, setShowModelPicker] = useState(false)

  const [panelWidth, setPanelWidth] = useState<string | null>(null)
  const [windowWidth, setWindowWidth] = useState(0)
  const [mobileTab, setMobileTab] = useState<'chat' | 'editor'>('chat')
  const containerRef = useRef<HTMLDivElement>(null)
  const isResizing = useRef(false)

  useEffect(() => {
    setWindowWidth(window.innerWidth)
    const onResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const handleColResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    isResizing.current = 'col'
    const startX = e.clientX
    const startWidth = containerRef.current?.getBoundingClientRect().width || window.innerWidth
    const initialPct = panelWidth ? parseFloat(panelWidth) / 100 : 0.5
    const onMove = (ev: MouseEvent) => {
      if (isResizing.current !== 'col') return
      const dx = ev.clientX - startX
      const pct = Math.max(0.2, Math.min(0.8, initialPct + dx / startWidth))
      setPanelWidth(`${(pct * 100).toFixed(1)}%`)
    }
    const onUp = () => {
      isResizing.current = false
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  const { token } = useAuthStore()
  const { balance } = useTokenStore()
  const [selectedModel, setSelectedModel] = useState(saved?.model || '')

  useEffect(() => {
    fetchModels()
    loadSavedMessages()
  }, [])

  const fetchModels = async () => {
    try {
      setModelsLoading(true)
      const res = await client.get('/models/ai', { params: { category: 'chat' } })
      const all = (res.data || []) as AiModelItem[]
      setAvailableModels(all)
      // Сортируем: сначала бесплатные, потом платные
      const sorted = [...all].sort((a, b) => (a.is_paid ? 1 : 0) - (b.is_paid ? 1 : 0))
      const firstFree = sorted[0]
      // Текущая модель платная или вообще не из списка — ставим первую бесплатную
      const currentModelIsPaid = all.find(m => m.model_name === selectedModel)?.is_paid
      if (firstFree && (!selectedModel || !all.some(m => m.model_name === selectedModel) || currentModelIsPaid)) {
        setSelectedModel(firstFree.model_name)
      }
    } catch { setAvailableModels([]) }
    finally { setModelsLoading(false) }
  }

  const loadSavedMessages = () => {
    const saved = loadState()
    if (saved?.messages) setMessages(saved.messages)
    if (saved?.editorCode !== undefined) setEditorCode(saved.editorCode)
    if (saved?.editorLang) setEditorLang(saved.editorLang)
    if (saved?.model) setSelectedModel(saved.model)
  }

  useEffect(() => {
    saveState({ messages, editorCode, editorLang, model: selectedModel, activeTab })
  }, [messages, editorCode, editorLang, selectedModel, activeTab])

  const visibleModels = useMemo(() => {
    const textModels = availableModels.filter(m => m.category === 'chat' || m.category === 'text')
    const free = textModels.filter(m => !m.is_paid)
    const paid = textModels.filter(m => m.is_paid)
    return [...free, ...paid]
  }, [availableModels])

  const currentModel = useMemo(
    () => availableModels.find(m => m.model_name === selectedModel) || null,
    [availableModels, selectedModel]
  )

  const handleSend = async () => {
    if (!input.trim() || isLoading) return
    const userMsg: CodeMessage = { id: Date.now().toString(), role: 'user', text: input }
    setMessages(prev => [...prev, userMsg])
    setInput('')
    setShowExample(false)
    setIsLoading(true)

    const assistantId = (Date.now()+1).toString()
    setMessages(prev => [...prev, { id: assistantId, role: 'assistant', text: '' }])

    let fullText = ''
    try {
      const token = useAuthStore.getState().token
      const res = await fetch('/code/chat/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: input,
          model: selectedModel || undefined,
          history: messages.slice(-20).map(m => ({ role: m.role, content: m.text })),
        }),
      })

      if (!res.ok) {
        setMessages(prev => prev.filter(m => m.id !== assistantId))
        toast.error('Ошибка соединения')
        setIsLoading(false)
        return
      }

      const reader = res.body?.getReader()
      if (!reader) {
        setMessages(prev => prev.filter(m => m.id !== assistantId))
        setIsLoading(false)
        return
      }

      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))
              if (data.type === 'token') {
                fullText += data.text
                setMessages(prev => prev.map(m =>
                  m.id === assistantId ? { ...m, text: fullText } : m
                ))
              } else if (data.type === 'done') {
                const text = data.fullText || fullText
                const codeBlocks: { language: string; code: string }[] = []
                const blockRegex = /```(\w*)\s*\n?([\s\S]*?)```/g
                let match
                let cleanText = text
                while ((match = blockRegex.exec(text)) !== null) {
                  const lang = match[1] || 'text'
                  const code = match[2].trim()
                  codeBlocks.push({ language: lang, code })
                  cleanText = cleanText.replace(match[0], '').trim()
                }

                if (codeBlocks.length > 0) {
                  const first = codeBlocks[0]
                  // Проверка: если код — HTML, парсим <script> из него
                  let cleanCode = first.code
                  if (first.language === 'html' || cleanCode.startsWith('<!') || cleanCode.startsWith('<html')) {
                    const scriptMatch = cleanCode.match(/<script[^>]*>([\s\S]*?)<\/script>/)
                    if (scriptMatch) {
                      cleanCode = scriptMatch[1].trim()
                    }
                  }
                  setEditorCode(cleanCode)
                  setShowExample(false)
                  if (EXECUTABLE_LANGUAGES.includes(first.language)) {
                    setEditorLang(first.language)
                  }
                  toast('Код вставлен в редактор')
                }

                setMessages(prev => prev.map(m =>
                  m.id === assistantId
                    ? { ...m, text: cleanText || (codeBlocks.length > 0 ? '' : text), blocks: codeBlocks.length > 0 ? codeBlocks : undefined }
                    : m
                ))
                if (data.tokensSpent > 0) toast.success(`-${data.tokensSpent} CN ⚡`)
              } else if (data.type === 'error') {
                setMessages(prev => prev.map(m =>
                  m.id === assistantId ? { ...m, text: '⚠️ ' + data.text } : m
                ))
                toast.error(data.text)
              }
            } catch {}
          }
        }
      }
    } catch (err: any) {
      setMessages(prev => prev.map(m =>
        m.id === assistantId ? { ...m, text: '⚠️ Ошибка: ' + (err?.message || 'соединение') } : m
      ))
    }
    finally { setIsLoading(false) }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  const clearChat = () => { setMessages([]); setEditorCode(''); setShowExample(true); setExecResult(null); setInput('') }

  const copyToClipboard = (text: string) => {
    try { navigator.clipboard.writeText(text); toast.success('Скопировано') }
    catch { toast.error('Не удалось скопировать') }
  }

  const downloadCode = (code: string, lang: string) => {
    const extMap: Record<string, string> = { python: 'py', javascript: 'js', typescript: 'ts', bash: 'sh', c: 'c', cpp: 'cpp', rust: 'rs', go: 'go', ruby: 'rb', php: 'php', java: 'java', r: 'r', perl: 'pl', swift: 'swift' }
    const ext = extMap[lang] || 'txt'
    const blob = new Blob([code], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `code.${ext}`
    a.click(); URL.revokeObjectURL(url)
  }

  const executeCode = async () => {
    if (!editorCode.trim() || isExecuting) return
    setIsExecuting(true)
    setExecResult(null)
    try {
      const res = await client.post('/code/execute', { language: editorLang, code: editorCode, stdin: '' })
      setExecResult(res.data)
    } catch (err: any) { setExecResult({ output: '', stderr: '', error: err?.response?.data?.detail || 'Ошибка выполнения', execution_time_ms: null }) }
    finally { setIsExecuting(false) }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    files.forEach(f => {
      const reader = new FileReader()
      reader.onload = () => {
        setAttachedFiles(prev => [...prev, { name: f.name, data: reader.result as string, mimetype: f.type }])
      }
      reader.readAsDataURL(f)
    })
    if (e.target) e.target.value = ''
  }

  const handleCaptureCamera = () => {
    const input = document.createElement('input')
    input.type = 'file'; input.accept = 'image/*'; input.capture = 'environment'
    input.onchange = (e: any) => {
      const f = e.target?.files?.[0]
      if (f) {
        const reader = new FileReader()
        reader.onload = () => setAttachedFiles(prev => [...prev, { name: f.name, data: reader.result as string, mimetype: f.type }])
        reader.readAsDataURL(f)
      }
    }
    input.click()
  }

  const handleAddPageLink = () => {
    const url = window.prompt('Введите URL:')
    if (url) setInput(prev => prev + ` ${url}`)
  }

  const removeFile = (idx: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== idx))
  }

  return (
    <div className="flex flex-col bg-[#0a0a15]" style={{ height: windowWidth >= 1024 ? '100vh' : 'auto', minHeight: '100vh' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/5 bg-[#0f0f1a]">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Code className="w-4 h-4 text-indigo-400" />
            <span className="text-sm text-gray-300 font-medium">AI Код</span>
          </div>
          <div className="relative">
            <button onClick={() => setShowModelPicker(!showModelPicker)}
              className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-white/5 hover:bg-white/10 text-[11px] text-gray-400 transition-colors whitespace-nowrap"
              disabled={modelsLoading}>
              {modelsLoading ? <Loader2 className="w-3 h-3 text-indigo-400 animate-spin" />
                : currentModel ? <>
                  {currentModel.is_paid ? <span className="text-[10px]">👑</span>
                    : <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 px-1 py-0.5 rounded">FREE</span>}
                  <span className={currentModel.is_paid ? 'text-indigo-400' : 'text-emerald-400'}>
                    {currentModel.display_name || currentModel.model_name.slice(0, 20)}
                  </span>
                </> : <span className="text-gray-600">Нет моделей</span>}
              <ChevronDown className="w-2.5 h-2.5 text-gray-600" />
            </button>
            {showModelPicker && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowModelPicker(false)} />
                <div className="absolute top-full left-0 mt-1 min-w-[340px] glass-card z-20 p-1.5 max-h-72 overflow-y-auto">
                  {visibleModels.length === 0 && <div className="text-xs text-gray-500 p-2">Нет доступных моделей</div>}
                  {visibleModels.map(m => {
                    const isPaidLocked = m.is_paid && (balance ?? 0) < 1000
                    return (
                    <button key={m.model_name}
                      onClick={() => {
                        if (isPaidLocked) { toast.error('Недостаточно токенов. Пополните баланс для доступа к платным моделям.'); return }
                        setSelectedModel(m.model_name); setShowModelPicker(false)
                      }}
                      className={`w-full text-left px-2 py-1.5 rounded text-[11px] transition-colors flex items-center justify-between ${
                        selectedModel === m.model_name ? 'bg-indigo-500/10 text-indigo-300' : isPaidLocked ? 'text-gray-500 cursor-not-allowed opacity-60' : 'text-gray-400 hover:bg-white/5'
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        {m.is_paid ? <span className="text-[11px]">👑</span> : (
                          <span className="text-[9px] font-bold text-emerald-400 bg-emerald-500/10 px-1 py-0.5 rounded">FREE</span>
                        )}
                        <span className={isPaidLocked ? 'text-gray-500' : m.is_paid ? 'text-indigo-400' : 'text-emerald-400'}>
                          {m.display_name || m.model_name}
                        </span>
                      </span>
                      <span className="text-[10px] text-gray-600">{m.provider}</span>
                    </button>
                    )}
                  )}
                </div>
              </>
            )}
          </div>
          {messages.length > 0 && <span className="text-[10px] text-gray-600">{messages.length} сообщ.</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={clearChat}
            className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-red-400 transition-colors">
            <Trash2 className="w-3 h-3" />
            Очистить чат
          </button>
        </div>
      </div>

      {/* Desktop tab bar - chat / editor / canvas */}
      {windowWidth >= 1024 && (
        <div className="flex items-center gap-0.5 px-4 py-1 border-b border-white/5 bg-[#0d0d1a] shrink-0">
          <button onClick={() => setActiveTab('chat')}
            className={`px-3 py-1 rounded text-[11px] transition-colors flex items-center gap-1.5 ${activeTab === 'chat' ? 'bg-indigo-500/15 text-indigo-300' : 'text-gray-500 hover:text-gray-300'}`}>
            <Code className="w-3 h-3" />
            Чат
          </button>
          <button onClick={() => setActiveTab('editor')}
            className={`px-3 py-1 rounded text-[11px] transition-colors flex items-center gap-1.5 ${activeTab === 'editor' ? 'bg-indigo-500/15 text-indigo-300' : 'text-gray-500 hover:text-gray-300'}`}>
            <Terminal className="w-3 h-3" />
            Редактор
          </button>
          <button onClick={() => setActiveTab('canvas')}
            className={`px-3 py-1 rounded text-[11px] transition-colors flex items-center gap-1.5 ${activeTab === 'canvas' ? 'bg-purple-500/15 text-purple-300' : 'text-gray-500 hover:text-gray-300'}`}>
            <Palette className="w-3 h-3" />
            🎨 Канвас
          </button>
        </div>
      )}

      {/* Mobile tab switcher */}
      {windowWidth < 1024 && (
        <div className="flex items-center gap-0.5 px-4 py-1.5 border-b border-white/5 bg-[#0d0d1a]">
          <button onClick={() => { setMobileTab('chat'); setActiveTab('chat') }}
            className={`px-3 py-1 rounded text-[11px] transition-colors ${mobileTab === 'chat' ? 'bg-indigo-500/15 text-indigo-300' : 'text-gray-500 hover:text-gray-300'}`}>
            Чат
          </button>
          <button onClick={() => { setMobileTab('editor'); setActiveTab('editor') }}
            className={`px-3 py-1 rounded text-[11px] transition-colors ${mobileTab === 'editor' ? 'bg-indigo-500/15 text-indigo-300' : 'text-gray-500 hover:text-gray-300'}`}>
            Редактор
          </button>
          <button onClick={() => { setMobileTab('editor'); setActiveTab('canvas') }}
            className={`px-3 py-1 rounded text-[11px] transition-colors ${mobileTab === 'editor' && activeTab === 'canvas' ? 'bg-purple-500/15 text-purple-300' : 'text-gray-500 hover:text-gray-300'}`}>
            🎨 Канвас
          </button>
        </div>
      )}

      <div ref={containerRef} className="flex-1 flex" style={{ flexDirection: windowWidth >= 1024 ? 'row' : 'column', overflow: windowWidth >= 1024 ? 'hidden' : undefined, minHeight: 0 }}>

        {/* CANVAS MODE - full width */}
        {activeTab === 'canvas' && (
          <CanvasPreview token={token} selectedModel={selectedModel} />
        )}

        {/* CHAT + EDITOR (hidden when canvas mode) */}
        {activeTab !== 'canvas' && (
        <>
        {/* LEFT — Chat */}
        <div className="flex flex-col" style={{ minWidth: 0, flexShrink: 0, width: windowWidth >= 1024 ? (panelWidth || '50%') : '100%', display: windowWidth < 1024 && mobileTab === 'editor' ? 'none' : undefined }}>
          <div ref={chatContainerRef} className="overflow-y-auto p-3 space-y-3 flex-1">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center px-4 py-6">
                <Code className="w-10 h-10 text-indigo-500/30 mb-2" />
                <h1 className="text-sm text-gray-300 mb-1">AI Помощник по коду</h1>
                <p className="text-[11px] text-gray-500 mb-4 leading-relaxed max-w-xs">
                  Опишите задачу — AI напишет код и вставит его в редактор.
                  Отредактируйте и нажмите <span className="text-emerald-400">Run</span> для выполнения.
                </p>
                <div className="w-full max-w-xs text-left">
                  <p className="text-[10px] text-gray-600 mb-2">Например, отправьте:</p>
                  <div className="rounded-lg border border-white/5 bg-[#0d0d1a] p-2.5 mb-4">
                    <pre className="text-[10px] text-gray-300 font-mono leading-relaxed whitespace-pre-wrap">напиши функцию сортировки
пузырьком на Python,
выведи результат и
объясни как работает</pre>
                  </div>
                  <p className="text-[10px] text-gray-600">
                    AI ответит, вставит код в правый редактор.
                    Можешь поправить код и нажать <span className="text-emerald-400">Run</span> ▶
                  </p>
                </div>
              </div>
            )}
            {messages.map(msg => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[90%] ${msg.role === 'user' ? 'bg-indigo-500/10 border border-indigo-500/20 rounded-2xl rounded-br-md px-3 py-2' : 'bg-white/5 border border-white/10 rounded-2xl rounded-bl-md px-3 py-2'}`}>
                  {msg.role === 'assistant' ? (
                    <>
                      {isLoading && !msg.text ? (
                        <div className="flex items-center gap-2 py-2">
                          <span className="inline-flex items-center gap-0.5">
                            <span className="w-1.5 h-1.5 bg-indigo-400/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                            <span className="w-1.5 h-1.5 bg-indigo-400/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                            <span className="w-1.5 h-1.5 bg-indigo-400/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                          </span>
                          <span className="text-[10px] text-indigo-400/60">Генерируется...</span>
                        </div>
                      ) : (
                        <div className="prose prose-invert prose-xs max-w-none">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {msg.text}
                          </ReactMarkdown>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-gray-300 whitespace-pre-wrap mb-2">{msg.text}</p>
                  )}
                  {msg.role === 'user' && (
                    <div className="flex items-center justify-end gap-1 mt-1">
                      <button onClick={() => copyToClipboard(msg.text)} className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors" title="Копировать"><Copy className="w-3 h-3" /></button>
                      <button onClick={() => setInput(msg.text)} className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-indigo-400 transition-colors" title="Редактировать"><Code className="w-3 h-3" /></button>
                      <button onClick={() => setMessages(prev => prev.filter(m => m.id !== msg.id))} className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-red-400 transition-colors" title="Удалить"><Trash2 className="w-3 h-3" /></button>
                      <button onClick={() => { const idx = messages.findIndex(m => m.id === msg.id); const nextIdx = idx + 1; const nextIsAssistant = messages[nextIdx]?.role === 'assistant'; setMessages(prev => prev.filter((_,i) => i === idx || !(i === nextIdx && nextIsAssistant) ? true : false)); }} className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-indigo-400 transition-colors" title="Перегенерировать"><RefreshCw className="w-3 h-3" /></button>
                    </div>
                  )}
                  {msg.blocks?.map((block, bi) => (
                    <div key={bi} className="mt-2 rounded-xl overflow-hidden border border-white/10 bg-[#0d0d1a]">
                      <div className="flex items-center justify-between px-3 py-1.5 bg-[#151525] border-b border-white/5">
                        <span className="text-[10px] text-indigo-400 font-mono">{LANGUAGE_NAMES[block.language] || block.language}</span>
                        <div className="flex items-center gap-1">
                          <button onClick={() => copyToClipboard(block.code)} className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors" title="Копировать"><Copy className="w-3 h-3" /></button>
                          <button onClick={() => { setEditorCode(block.code); setEditorLang(block.language) }} className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-indigo-400 transition-colors" title="Вставить в редактор"><Code className="w-3 h-3" /></button>
                          <button onClick={() => downloadCode(block.code, block.language)} className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors" title="Скачать"><Download className="w-3 h-3" /></button>
                        </div>
                      </div>
                      <pre className="p-3 overflow-x-auto text-xs leading-relaxed"><code className="text-gray-300">{block.code}</code></pre>
                    </div>
                  ))}
                  {msg.role === 'assistant' && (
                    <div className="flex items-center justify-end gap-1 mt-2 pt-2 border-t border-white/5">
                      <button onClick={() => copyToClipboard(msg.text)} className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors" title="Копировать текст"><Copy className="w-3 h-3" /></button>
                      <button onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Ссылка скопирована') }} className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors" title="Поделиться"><Share2 className="w-3 h-3" /></button>
                      <button onClick={() => setMessages(prev => prev.filter(m => m.id !== msg.id))} className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-red-400 transition-colors" title="Удалить"><Trash2 className="w-3 h-3" /></button>
                      <button onClick={() => { const idx = messages.findIndex(m => m.id === msg.id); if (idx > 0) { const userMsg = messages[idx-1]; setInput(userMsg.text); setMessages(prev => prev.filter((_,i) => i !== idx && i !== idx-1)) } }} className="p-1 rounded hover:bg-white/5 text-gray-500 hover:text-indigo-400 transition-colors" title="Перегенерировать"><RefreshCw className="w-3 h-3" /></button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {attachedFiles.length > 0 && (
            <div className="px-3 py-1.5 border-t border-white/5 bg-[#0d0d1a] flex flex-wrap gap-2">
              {attachedFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-white/5 border border-white/5 text-xs text-gray-400">
                  {f.mimetype.startsWith('image/') ? <img src={f.data} alt={f.name} className="w-4 h-4 rounded object-cover" />
                    : <FileText className="w-3 h-3 text-indigo-400" />}
                  <span className="max-w-[100px] truncate">{f.name}</span>
                  <button onClick={() => removeFile(i)} className="text-gray-600 hover:text-red-400 ml-0.5"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-start gap-1.5 px-3 py-2 border-t border-white/5 bg-[#0f0f1a]">
            <div className="relative">
              <button onClick={() => setShowAttachMenu(!showAttachMenu)}
                className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors" title="Прикрепить">
                <Paperclip className="w-4 h-4" />
              </button>
              {showAttachMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowAttachMenu(false)} />
                  <div className="absolute bottom-full left-0 mb-2 w-44 glass-card z-20 p-1.5">
                    <button onClick={() => { fileInputRef.current?.click(); setShowAttachMenu(false) }}
                      className="w-full text-left flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs text-gray-300 hover:text-white hover:bg-white/5 transition-all">
                      <Upload className="w-3.5 h-3.5 text-indigo-400" /><span>Загрузить файлы</span>
                    </button>
                    <button onClick={() => { handleCaptureCamera(); setShowAttachMenu(false) }}
                      className="w-full text-left flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs text-gray-300 hover:text-white hover:bg-white/5 transition-all">
                      <Camera className="w-3.5 h-3.5 text-purple-400" /><span>Захват камерой</span>
                    </button>
                    <button onClick={() => { handleAddPageLink(); setShowAttachMenu(false) }}
                      className="w-full text-left flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs text-gray-300 hover:text-white hover:bg-white/5 transition-all">
                      <Link className="w-3.5 h-3.5 text-blue-400" /><span>Добавить ссылку</span>
                    </button>
                  </div>
                </>
              )}
              <input ref={fileInputRef} type="file" onChange={handleFileSelect} className="hidden" multiple accept="*/*" />
            </div>
            <div className="flex-1 flex items-center gap-1.5">
              <textarea ref={textareaRef} value={input} onChange={e => { setInput(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px' }}
                onKeyDown={handleKeyDown} placeholder="Напиши функцию сортировки на Python..."
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-1.5 text-xs text-gray-300 placeholder-gray-600 resize-none focus:outline-none focus:border-indigo-500/30 min-h-[34px] max-h-[200px]" rows={1} />
              <button onClick={handleSend} disabled={isLoading || !input.trim()}
                className="p-1.5 rounded-xl bg-indigo-500/20 border border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                <Send className="w-4 h-4" />
              </button>
            </div>
            <button onClick={clearChat}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[10px] text-gray-500 hover:text-red-400 hover:bg-white/5 transition-colors shrink-0" title="Очистить диалог">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>

        {windowWidth >= 1024 && <span style={{ width: 6, cursor: 'col-resize', flexShrink: 0, position: 'relative', zIndex: 10 }}
          className="bg-transparent hover:bg-indigo-500/20 active:bg-indigo-500/30 transition-colors"
          onMouseDown={handleColResizeStart} />}

        </>
        )}

        {/* RIGHT — Editor */}
        {activeTab !== 'canvas' && (
        <div className="flex flex-col flex-1" style={{ minWidth: 0, display: windowWidth < 1024 && mobileTab === 'chat' ? 'none' : undefined }}>
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/5 bg-[#0f0f1a]">
            <div className="relative">
              <button onClick={() => setShowLangPicker(!showLangPicker)}
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-xs text-gray-300 transition-colors">
                <Terminal className="w-3 h-3 text-indigo-400" />
                {LANGUAGE_NAMES[editorLang] || editorLang}
                <ChevronDown className="w-3 h-3 text-gray-500" />
              </button>
              {showLangPicker && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowLangPicker(false)} />
                  <div className="absolute top-full left-0 mt-1 w-40 glass-card z-20 p-1.5 max-h-60 overflow-y-auto">
                    {EXECUTABLE_LANGUAGES.map(l => (
                      <button key={l} onClick={() => { setEditorLang(l); setShowLangPicker(false) }}
                        className={`w-full text-left px-2 py-1 rounded text-xs transition-colors ${editorLang === l ? 'bg-indigo-500/10 text-indigo-300' : 'text-gray-400 hover:bg-white/5'}`}>
                        {LANGUAGE_NAMES[l] || l}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => copyToClipboard(editorCode)} className="p-1.5 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors" title="Копировать"><Copy className="w-3.5 h-3.5" /></button>
              <button onClick={() => downloadCode(editorCode, editorLang)} className="p-1.5 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors" title="Скачать"><Download className="w-3.5 h-3.5" /></button>
              <button onClick={() => setEditorCode('')} className="p-1.5 rounded hover:bg-white/5 text-gray-500 hover:text-red-400 transition-colors" title="Очистить"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          <div className="flex flex-col" style={{ flex: 1, minHeight: 0 }}>
            <div className="relative overflow-hidden" style={{ flex: 1, minHeight: 0 }}>
              {showExample && !editorCode && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-4 bg-[#0d0d1a]" onClick={() => setShowExample(false)}>
                  <pre className="text-[11px] text-gray-400 font-mono leading-relaxed whitespace-pre-wrap border border-white/5 rounded-lg p-3 bg-[#0a0a15] w-full max-w-sm">{`def bubble_sort(arr):
    n = len(arr)
    for i in range(n):
        for j in range(0, n-i-1):
            if arr[j] > arr[j+1]:
                arr[j], arr[j+1] = arr[j+1], arr[j]
    return arr

arr = [64, 34, 25, 12, 22, 11, 90]
sorted_arr = bubble_sort(arr)
print(f"Отсортировано: {sorted_arr}")`}</pre>
                  <p className="text-[10px] text-gray-600 mt-2">Нажмите <span className="text-emerald-400">Run</span> чтобы выполнить пример</p>
                </div>
              )}
              <textarea value={editorCode} onChange={e => { setEditorCode(e.target.value); setShowExample(false) }}
                placeholder="// Напишите код сюда"
                className="absolute inset-0 w-full h-full bg-[#0d0d1a] text-xs text-gray-300 font-mono p-4 resize-none focus:outline-none placeholder-gray-700 leading-relaxed" spellCheck={false} />
            </div>

            {/* Resizer for output */}
            <div
              onMouseDown={(e) => {
                e.preventDefault()
                isResizingOutput.current = true
                const startY = e.clientY
                const el = editorOutputRef.current
                const startH = el?.getBoundingClientRect().height || 100
                const onMove = (ev: MouseEvent) => {
                  if (!isResizingOutput.current || !el) return
                  const dy = startY - ev.clientY
                  const newH = Math.max(60, Math.min(400, startH + dy))
                  el.style.height = newH + 'px'
                }
                const onUp = () => {
                  isResizingOutput.current = false
                  document.removeEventListener('mousemove', onMove)
                  document.removeEventListener('mouseup', onUp)
                  document.body.style.cursor = ''
                  document.body.style.userSelect = ''
                }
                document.addEventListener('mousemove', onMove)
                document.addEventListener('mouseup', onUp)
                document.body.style.cursor = 'row-resize'
                document.body.style.userSelect = 'none'
              }}
              style={{ height: 4, cursor: 'row-resize', flexShrink: 0 }}
              className="bg-transparent hover:bg-indigo-500/20 active:bg-indigo-500/30 transition-colors"
            />

            <div ref={editorOutputRef} style={{ height: execResult ? 200 : 'auto', flexShrink: 0, overflow: 'hidden' }} className="px-3 py-2 border-t border-white/5 bg-[#0f0f1a]">
              <div className="flex items-center gap-2">
                <button onClick={executeCode} disabled={isExecuting || !editorCode.trim()}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/20 text-emerald-400 text-xs hover:bg-emerald-500/25 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                  {isExecuting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                  {isExecuting ? 'Выполнение...' : 'Run'}
                </button>
                {execResult?.execution_time_ms != null && <span className="text-[10px] text-gray-600">{execResult.execution_time_ms}ms</span>}
              </div>
              {execResult && (
                <div className="border-t border-white/5 mt-2">
                  <div className="flex items-center justify-between px-3 py-1.5 bg-[#0d0d1a]">
                    <span className="text-[10px] text-gray-500 font-mono">Output</span>
                    <div className="flex items-center gap-2">
                      {execResult.error ? <span className="text-[10px] text-red-400">{execResult.error}</span>
                        : <span className="text-[10px] text-emerald-500">✓</span>}
                      <button onClick={() => setExecResult(null)} className="p-0.5 rounded hover:bg-white/5 text-gray-600 hover:text-gray-400"><X className="w-3 h-3" /></button>
                    </div>
                  </div>
                  <pre className="p-3 max-h-[200px] overflow-y-auto text-xs text-gray-300 font-mono leading-relaxed whitespace-pre-wrap">
                    {execResult.output || execResult.stderr || <span className="text-gray-600 italic">(пустой вывод)</span>}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  )
}
